/**
 * The WIS-backed client multi-select used everywhere a Space's client scope is
 * chosen (DV-501): picking the initial set on the create path, and adding to an
 * existing scoped Space in `ClientScopePanel`.
 *
 * Replaces picking from `ReferenceData.clients`, which is presentational client
 * *names* rather than a backed catalog — and is empty on the routed path, so a
 * Space could be saved against clients the API can't resolve. That was the
 * original reason client scoping was switched off.
 *
 * WHY THE SELECTION CARRIES BOTH ID FORMS
 * ---------------------------------------------------------------------------
 * Same reason `DocumentBrowser`'s reference column filters do (see
 * `referenceFilters.tsx`), and it is not optional here. `ClientSearch` reports
 * relay GlobalIDs, but `space_clients.client_id` holds a bare arena pk. Feeding
 * bare pks back in as `values` breaks the component in two ways, because it
 * matches them against its own option ids, which are GlobalIDs:
 *
 *   - chips render as the `Client <id>` placeholder instead of the client's name
 *     (`u.find(o => o.id === e)?.label` misses, as does its label cache);
 *   - its dedupe guard `values.includes(picked)` never matches, so re-picking a
 *     client already in the set appends a duplicate.
 *
 * So the picker reports `ids` (bare, for the wire) alongside `globalIds` (what
 * `ClientSearch` needs handed back to render its own chips).
 *
 * `ClientSearch` fetches its own arenas and therefore needs an `ApolloProvider`
 * above it, which is why every caller mounts it behind an opt-in prop rather
 * than by default — see `UploadDocumentModal`'s `referenceSearch`.
 */
import React from "react";
import { ClientSearch } from "@jsq/common-components";
import { toBareReferenceId } from "#common/globalId";

/** A client selection in both the form the wire needs and the form the UI needs. */
export type ClientSelection = {
  /** Bare arena pks — what every Space client write path sends. */
  ids: string[];
  /** Relay GlobalIDs — what `ClientSearch` needs back to render its chips. */
  globalIds: string[];
};

export const EMPTY_CLIENT_SELECTION: ClientSelection = { ids: [], globalIds: [] };

/** Build a selection from the GlobalIDs `ClientSearch` reported. */
export const clientSelectionFrom = (globalIds: string[]): ClientSelection => ({
  ids: globalIds.map(toBareReferenceId),
  globalIds,
});

export type ClientPickerProps = {
  /** The current selection. Pass {@link EMPTY_CLIENT_SELECTION} for none. */
  value: ClientSelection;
  onChange: (selection: ClientSelection) => void;
  placeholder?: string;
  testId?: string;
};

export function ClientPicker({
  value,
  onChange,
  placeholder = "Search for clients",
  testId = "space-client-search",
}: ClientPickerProps) {
  return (
    <ClientSearch
      multiselect
      // GlobalIDs, deliberately — see the module header.
      values={value.globalIds}
      isFullWidth
      placeholder={placeholder}
      testId={testId}
      // `multiselect` routes selections through `onMultiSelect`; `onSelect` is
      // required by the prop type and never fires in this mode.
      onSelect={(globalId) => onChange(clientSelectionFrom(globalId ? [globalId] : []))}
      onMultiSelect={(globalIds) => onChange(clientSelectionFrom(globalIds))}
      onSearchClear={() => onChange(EMPTY_CLIENT_SELECTION)}
    />
  );
}
